import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  East as ArrowIcon,
} from '@mui/icons-material';
import { loginSuccess } from '../store/authSlice';
import { authService } from '../services/api';
import logo from '../assets/logo.png';

export const ROLE_LABELS = {
  admin: 'Super Admin',
  super_admin: 'Super Admin',
  amministratore: 'CapoCommessa',
  capocantiere: 'Capocantiere',
  tecnico: 'Tecnico',
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState(null); // ruoli tra cui scegliere (doppio ruolo)

  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      setFormData(prev => ({ ...prev, username: savedUsername }));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(formData);
      localStorage.setItem('rememberedUsername', formData.username);
      const u = response.data.user;

      // Doppio ruolo: se l'utente ha più ruoli, fagli scegliere con quale entrare
      if (u.roles && u.roles.length > 1) {
        setRoleOptions(u.roles);
        setLoading(false);
        return;
      }

      dispatch(loginSuccess({
        user: u,
        token: response.data.access_token,
        activeRole: response.data.active_role,
      }));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Credenziali non valide');
      setLoading(false);
    }
  };

  const handleSelectRole = async (role) => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.login({ ...formData, role });
      dispatch(loginSuccess({
        user: response.data.user,
        token: response.data.access_token,
        activeRole: response.data.active_role,
      }));
      navigate('/dashboard');
    } catch (err) {
      setError('Errore nella selezione del ruolo, riprova.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#0a0a0a',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Sfondo con pattern geometrico sottile */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Accento rosso laterale */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: { xs: '100%', md: '45%' },
          height: { xs: '35%', md: '100%' },
          background: 'linear-gradient(135deg, #e50000 0%, #990000 100%)',
          clipPath: {
            xs: 'polygon(0 0, 100% 0, 100% 70%, 0 100%)',
            md: 'polygon(0 0, 85% 0, 65% 100%, 0 100%)'
          },
          zIndex: 0,
        }}
      />

      {/* Contenuto principale */}
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          py: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 4, md: 8 },
            width: '100%',
            maxWidth: 1000,
          }}
        >
          {/* Sezione Brand - Sinistra */}
          <Box
            sx={{
              flex: { xs: 'none', md: 1 },
              textAlign: { xs: 'center', md: 'left' },
              color: 'white',
              px: { xs: 2, md: 0 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                mb: 3,
                mx: { xs: 'auto', md: 0 },
                backgroundColor: '#000',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              }}
            >
              <img
                src={logo}
                alt="NC Elettrica"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 1,
                letterSpacing: '-0.02em',
              }}
            >
              NC Elettrica
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '1rem', md: '1.1rem' },
                opacity: 0.9,
                mb: 3,
              }}
            >
              Sistema Gestione Cantieri
            </Typography>

            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                mt: 4,
                pt: 4,
                borderTop: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                Gestisci interventi, timbrature e rapportini
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                in un'unica piattaforma professionale.
              </Typography>
            </Box>
          </Box>

          {/* Sezione Form - Destra */}
          <Paper
            elevation={0}
            sx={{
              flex: { xs: 'none', md: 1 },
              width: { xs: '100%', md: 'auto' },
              maxWidth: 400,
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              backgroundColor: 'white',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1a1a1a',
                mb: 0.5,
              }}
            >
              {roleOptions ? 'Scegli il ruolo' : 'Bentornato'}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 3,
              }}
            >
              {roleOptions
                ? 'Il tuo account ha più ruoli: entra con quello che ti serve'
                : 'Inserisci username o email per accedere'}
            </Typography>

            {/* Errore */}
            <Collapse in={!!error}>
              <Alert
                severity="error"
                onClose={() => setError('')}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { alignItems: 'center' },
                }}
              >
                {error}
              </Alert>
            </Collapse>

            {/* Selettore ruolo (doppio ruolo) */}
            {roleOptions && (
              <Box>
                {roleOptions.map((r) => (
                  <Button
                    key={r}
                    fullWidth
                    variant="outlined"
                    onClick={() => handleSelectRole(r)}
                    disabled={loading}
                    startIcon={<Person sx={{ color: '#e50000' }} />}
                    sx={{
                      mb: 1.5,
                      py: 1.5,
                      borderRadius: 2,
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderColor: '#eee',
                      color: '#1a1a1a',
                      '&:hover': { borderColor: '#e50000', backgroundColor: 'rgba(229,0,0,0.04)' },
                    }}
                  >
                    {ROLE_LABELS[r] || r}
                  </Button>
                ))}
                <Button
                  fullWidth
                  onClick={() => { setRoleOptions(null); setFormData((p) => ({ ...p, password: '' })); }}
                  sx={{ mt: 1, textTransform: 'none', color: '#999' }}
                >
                  ← Usa un altro account
                </Button>
              </Box>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: roleOptions ? 'none' : 'block' }}>
              <TextField
                fullWidth
                id="username"
                label="Username o Email"
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: '#ddd' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#e50000',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#e50000',
                  },
                }}
              />

              <TextField
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: '#999' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: '#ddd' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#e50000',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#e50000',
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !formData.username || !formData.password}
                endIcon={!loading && <ArrowIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#e50000',
                  boxShadow: '0 4px 14px 0 rgba(229, 0, 0, 0.39)',
                  '&:hover': {
                    backgroundColor: '#c50000',
                    boxShadow: '0 6px 20px rgba(229, 0, 0, 0.5)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#999',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Accedi'
                )}
              </Button>
            </Box>

            {/* Footer card */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#999' }}>
                © {new Date().getFullYear()} NC Elettrica
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Footer assoluto */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.7rem',
          }}
        >
          Sviluppato da Spinotto Web Agency
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
